/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { IServiceDeviceData } from "../../../store/api/devices/types";

export interface IDeviceMarker {
  objectKey: string;
  name: string;
  position: {
    lat: number;
    lng: number;
  };
  color: string;
  device: IServiceDeviceData;
  serialNumber?: string;
  connectionStatus: string;
}

export interface IFilteredMarkers {
  visibleMarkers: IDeviceMarker[];
  outlierMarkers: IDeviceMarker[];
  filteredCount: number;
}

/**
 * Calculate distance between two coordinates in kilometers using Haversine formula
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Filters markers to only show devices within US coordinates
 * US bounds include continental US, Alaska, Hawaii, and territories
 * @param markers All device markers
 * @returns Object containing visible markers (in US) and outlier markers (outside US)
 */
export const filterOutlierMarkers = (
  markers: IDeviceMarker[], 
  maxDistance: number = 500,
  minClusterSize: number = 2
): IFilteredMarkers => {
  if (markers.length === 0) {
    return {
      visibleMarkers: [],
      outlierMarkers: [],
      filteredCount: 0
    };
  }

  // US bounds (includes Alaska, Hawaii, and territories)
  const US_BOUNDS = {
    minLat: 15,    // Covers Hawaii and territories
    maxLat: 72,    // Covers Alaska
    minLng: -180,  // Covers Alaska (Aleutian Islands cross date line)
    maxLng: -60    // Covers Puerto Rico and eastern territories
  };

  const visibleMarkers: IDeviceMarker[] = [];
  const outlierMarkers: IDeviceMarker[] = [];

  markers.forEach((marker) => {
    const { lat, lng } = marker.position;
    
    // Check if coordinates are within US bounds
    const isInUS = 
      lat >= US_BOUNDS.minLat && 
      lat <= US_BOUNDS.maxLat && 
      lng >= US_BOUNDS.minLng && 
      lng <= US_BOUNDS.maxLng;
    
    if (isInUS) {
      visibleMarkers.push(marker);
    } else {
      outlierMarkers.push(marker);
    }
  });

  console.log(`Filtered ${visibleMarkers.length} devices with location out of ${markers.length} total devices`);
  if (outlierMarkers.length > 0) {
    console.log(`Filtered out ${outlierMarkers.length} devices outside US coordinates`);
  }

  return {
    visibleMarkers,
    outlierMarkers,
    filteredCount: outlierMarkers.length
  };
};

/**
 * Gets the connection status text from device status code
 * Status 0 is NORMAL (connected), status 19 is TIMEOUT (disconnected)
 */
export const getConnectionStatusText = (device: IServiceDeviceData): string => {
  const isDisconnected = device.status === 19;
  return isDisconnected ? "Disconnected" : "Connected";
};

/**
 * Determines marker color based on device connection status and alarm counts
 * Green: Connected with no alarms
 * Red: Connected with critical alarms
 * Orange: Connected with major alarms (no critical)
 * Grey: Disconnected
 * 
 * Note: status 0 is NORMAL (connected), status 19 is TIMEOUT (disconnected)
 */
export const getMarkerColor = (device: IServiceDeviceData): string => {
  // Check if device is disconnected (status 19 is disconnected/timeout)
  // Status 0 is NORMAL/connected
  const isDisconnected = device.status === 19;
  
  if (isDisconnected) {
    return "#9E9E9E"; // Grey - disconnected
  }

  if (device.criticalAlarmCount > 0) {
    return "#F44336"; // Red - critical alarm
  }

  if (device.majorAlarmCount > 0) {
    return "#FF9800"; // Orange - major alarm
  }

  return "#4CAF50"; // Green - connected, no alarms
};

/**
 * Converts microdegrees to decimal degrees
 * Backend appears to return values already divided by 10000 (e.g., 3892.4542)
 * Need to divide by additional 100 to get decimal degrees (38.924542°)
 */
const microdegreesToDecimal = (microdegrees: number | string): number => {
  const value = typeof microdegrees === 'number' ? microdegrees : parseFloat(microdegrees);
  // Values come as degrees*100, need to divide by 100 to get decimal degrees
  return value / 100;
};

/**
 * Filters devices that have valid latitude and longitude coordinates
 */
export const filterDevicesWithLocation = (devices: IServiceDeviceData[]): IServiceDeviceData[] => {
  const filtered = devices.filter((device) => {
    // First check if both latitude and longitude exist
    if (device.latitude === undefined || device.latitude === null || 
        device.longitude === undefined || device.longitude === null) {
      return false;
    }
    
    // Convert to string for parsing
    const latStr = typeof device.latitude === 'number' ? device.latitude.toString() : device.latitude || "";
    const lngStr = typeof device.longitude === 'number' ? device.longitude.toString() : device.longitude || "";
    const latRaw = parseFloat(latStr);
    const lngRaw = parseFloat(lngStr);
    
    // Check if parsing succeeded
    if (isNaN(latRaw) || isNaN(lngRaw)) {
      return false;
    }
    
    // Apply microdegree conversion
    const lat = microdegreesToDecimal(latRaw);
    const lng = microdegreesToDecimal(lngRaw);
    
    // Check for valid coordinates after conversion
    // Must not be zero and must be within valid geographic ranges
    const hasValidCoords = lat !== 0 && lng !== 0 &&
                           lat >= -90 && lat <= 90 &&
                           lng >= -180 && lng <= 180;
    
    // Debug: Log first few devices to see what data we're getting
    if (devices.indexOf(device) < 3) {
      console.log("Sample device data:", {
        name: device.name,
        latitudeRaw: device.latitude,
        longitudeRaw: device.longitude,
        latType: typeof device.latitude,
        lngType: typeof device.longitude,
        parsed: { latRaw, lngRaw },
        converted: { lat, lng },
        hasValidCoords
      });
    }
    
    return hasValidCoords;
  });
  
  console.log(`Filtered ${filtered.length} devices with location out of ${devices.length} total devices`);
  return filtered;
};

/**
 * Transforms device data into marker format for Google Maps
 */
export const prepareDeviceMarkers = (devices: IServiceDeviceData[]): IDeviceMarker[] => {
  const devicesWithLocation = filterDevicesWithLocation(devices);

  return devicesWithLocation.map((device) => {
    // Convert from microdegrees to decimal degrees
    const lat = microdegreesToDecimal(device.latitude || 0);
    const lng = microdegreesToDecimal(device.longitude || 0);
    
    return {
      objectKey: device.objectKey,
      name: device.name,
      position: {
        lat,
        lng,
      },
      color: getMarkerColor(device),
      device,
      serialNumber: undefined, // Will be fetched when needed from full device details
      connectionStatus: getConnectionStatusText(device),
    };
  });
};

/**
 * Calculates the map bounds to fit all markers
 * Returns center and zoom level
 */
export const calculateMapBounds = (markers: IDeviceMarker[]) => {
  if (markers.length === 0) {
    return {
      center: { lat: 0, lng: 0 },
      zoom: 2,
    };
  }

  if (markers.length === 1) {
    return {
      center: markers[0].position,
      zoom: 14,
    };
  }

  const lats = markers.map((m) => m.position.lat);
  const lngs = markers.map((m) => m.position.lng);

  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);

  const center = {
    lat: (minLat + maxLat) / 2,
    lng: (minLng + maxLng) / 2,
  };

  // Calculate zoom level based on bounds
  const latDiff = maxLat - minLat;
  const lngDiff = maxLng - minLng;
  const maxDiff = Math.max(latDiff, lngDiff);

  let zoom = 14;
  if (maxDiff > 180) zoom = 3;
  else if (maxDiff > 90) zoom = 4;
  else if (maxDiff > 45) zoom = 5;
  else if (maxDiff > 22) zoom = 6;
  else if (maxDiff > 11) zoom = 7;
  else if (maxDiff > 5) zoom = 8;
  else if (maxDiff > 2.5) zoom = 9;
  else if (maxDiff > 1.25) zoom = 10;
  else if (maxDiff > 0.6) zoom = 11;
  else if (maxDiff > 0.3) zoom = 12;
  else if (maxDiff > 0.15) zoom = 13;

  return { center, zoom };
};

/**
 * Gets the legend items for map color codes
 */
export const getMapLegendItems = () => [
  { label: "Connected - No Alarms", color: "#4CAF50" },
  { label: "Connected - Critical Alarm", color: "#F44336" },
  { label: "Connected - Major Alarm", color: "#FF9800" },
  { label: "Disconnected", color: "#9E9E9E" },
];

/**
 * Interface for tooltip data structure
 */
export interface IMarkerTooltipData {
  name: string;
  serialNumber?: string;
  connectionStatus: string;
  criticalAlarmCount: number;
  majorAlarmCount: number;
}

/**
 * Prepares tooltip data for a device marker
 */
export const getMarkerTooltipData = (marker: IDeviceMarker): IMarkerTooltipData => {
  return {
    name: marker.name,
    serialNumber: marker.serialNumber || "N/A",
    connectionStatus: marker.connectionStatus,
    criticalAlarmCount: marker.device.criticalAlarmCount,
    majorAlarmCount: marker.device.majorAlarmCount,
  };
};
